import { useState, useMemo } from "react";
import { Heading, DataTable, DateDisplay, Button, Switch } from "@aidkitorg/component-library";
import { useContext } from "react";
import { UserInfoContext } from "../../Context";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { usePost } from "../../API";
import { ThreeColumnPage } from "../../Components/ThreeColumnPage";
import { useHistory } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { AIConversationAnalytics } from "@aidkitorg/aidkit/lib/convoai/schemas/Conversation";
import { formatDuration } from "../../utils/time";
import { useLocalizedStrings } from "../../Localization";
import { useManualPagination, useManualSorting } from "../../Hooks/DataTable";

type ConversationRow = AIConversationAnalytics;

export function ConversationReviewList() {
  const L = useLocalizedStrings();
  return (
    <div className="py-8 px-4">
      <Heading level="h1">{L.ai_coach.dashboard.review_queue}</Heading>          
      <ConversationReviewListInner />          
    </div>
  );
}

function ConversationReviewListInner() { 
  const [loading, setLoading] = useState<boolean>(false);
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const userInfo = useContext(UserInfoContext);
  const getConversations = usePost('/coach/conversation/explore');
  const history = useHistory();
  const [hideScoredByMe, setHideScoredByMe] = useState<boolean>(true);
  const L = useLocalizedStrings();
  const { manualPagination, setTotalRows } = useManualPagination({
    pageSize: 25,
    page: 1
  });
  const manualSorting = useManualSorting({
    initialSorting: [
      {
        id: "created_at",
        desc: true
      }
    ]
  });
      
  const columns = useMemo(() => { 
    const cols: ColumnDef<ConversationRow>[] = [
      {
        header: "ID",
        maxSize: 60,
        accessorKey: "id",
      },
      {
        header: L.ai_coach.dashboard.actions,
        id: "actions",
        cell: ({ row }: { row: { original: ConversationRow } }) => (
          <Button variant="primary" size="sm" onClick={() => history.push(`/ai/config/review/${row.original.id}`)}>
            Add/edit scores
          </Button>
        )
      },      
      {
        header: L.ai_coach.dashboard.created,
        accessorKey: "created_at",
        cell: ({ row }: { row: { original: ConversationRow } }) => (
          <DateDisplay
            utcValue={row.original.created_at}
            className="text-sm"
            optionOverrides={{ dateStyle: 'long', timeStyle: 'short', timeZoneName: undefined }}
            displayInUTC={false}
          />
        )
      },
      { header: L.ai_coach.dashboard.applicant, accessorKey: "legal_name" },
      {
        header: L.ai_coach.dashboard.duration,
        accessorKey: "duration",
        id: "duration",
        cell: ({ row }: { row: { original: ConversationRow } }) => (
          <span className="text-sm">{formatDuration(row.original.duration, 'seconds')}</span>
        )
      },
      {
        header: () => <>User<br /> 👍</>,
        maxSize: 60,        
        id: "feedback_positive",
        accessorKey: "feedback_positive",
      },
      {
        header: () => <>User<br /> 👎</>,
        maxSize: 60,        
        id: "feedback_negative",
        accessorKey: "feedback_negative",
      },
      {
        header: () => <>Reviewer<br /> ✅</>,
        maxSize: 80,        
        id: "scores_pass",
        accessorKey: "scores_pass",
      },
      {
        header: () => <>Reviewer<br/> ❌</>,
        maxSize: 80,        
        id: "scores_fail",
        accessorKey: "scores_fail",
      },
      {
        header: "Reviewed",
        size: 50,
        id: "scored_at",
        accessorKey: "scored_at",
        cell: ({ row }: { row: { original: ConversationRow } }) => (
          row.original.scored_at ? <DateDisplay
            utcValue={row.original.scored_at!}
            className="text-sm"
            optionOverrides={{ dateStyle: 'long', timeStyle: 'short', timeZoneName: undefined }}
            displayInUTC={false}
          /> : "--"
        )
      }      
    ];
    return cols;    
  }, [history]);
  
  useAsyncEffect(async () => {
    setLoading(true);
    try {
      const response = await getConversations({
        limit: manualPagination.pageSize,
        offset: manualPagination.pageSize * manualPagination.pageIndex,
        sortColumn: manualSorting.sorting[0]?.id as Parameters<typeof getConversations>[0]["sortColumn"],
        sortAsc: !manualSorting.sorting[0]?.desc,
        hideScoredByMe
      });
            
      if ('results' in response) {
        setTotalRows(response.count);
        const items: ConversationRow[] = response.results.map((conv) => {
          const withIdsAndDates = conv as { id: string | number, created_at: string | number | Date, updated_at: string | number | Date, scored_at: string | number | Date };
          const item: ConversationRow = {
            id: String(withIdsAndDates.id),
            created_at: new Date(withIdsAndDates.created_at as string | number),
            updated_at: new Date(withIdsAndDates.updated_at as string | number),
            created_by: conv.created_by,
            tokens_total: Number(conv.tokens_total),
            from_url: conv.from_url,
            legal_name: conv.legal_name,
            unsubmitted: conv.unsubmitted,
            duration: Number(conv.duration),
            feedback_positive: Number(conv.feedback_positive),
            feedback_negative: Number(conv.feedback_negative),
            scores_pass: Number(conv.scores_pass),
            scores_fail: Number(conv.scores_fail),
            scored_at: withIdsAndDates.scored_at !== null ? new Date(withIdsAndDates.scored_at as string) : null,
            transcript: (conv as any).transcript || null,
            feedback_reason: (conv as any).feedback_reason || null,
            total_count: conv.total_count,            
          };
          return item;
        });
        setConversations(items);
      }
    } finally {
      setLoading(false);
    }
  }, [userInfo?.uid, hideScoredByMe, manualPagination.pageSize, manualPagination.pageIndex, manualSorting.sorting]);
  
  return <div className="flex flex-col gap-2">
    <Switch 
      label="Hide conversations with scores" 
      checked={hideScoredByMe}
      onCheckedChange={(v) => setHideScoredByMe(v)}/>
    <DataTable
      className="mb-10"
      noDataMessage={L.ai_coach.dashboard.no_conversations_review}
      loading={loading}
      pageSize={manualPagination.pageSize}
      showPagination={true}
      resizableColumns={true}
      columnPinningState={{ left: ['id'] }}
      data={conversations}
      columns={columns}
      manualPagination={manualPagination}
      manualSorting={manualSorting}
    />
  </div>  
}
